import { type CSSProperties } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  useBrandTokens,
  useSidebarState,
  useResizableSidebar,
  SIDEBAR_COLLAPSED_WIDTH,
  TopBar,
  NavIcon,
  ContactRow,
  ResizeGutter,
  ClinicModePills,
  orgName,
  orgLogoUrl,
  orgLogoHorizontalUrl,
  orgPhone,
  orgEmail,
} from './shared';
import { useVisibleNav } from '../auth/useVisibleNav';
import { usePinnedRoutes, ROUTE_TO_PIN_KEY } from './usePinnedRoutes';

// ─── Clinic Sidebar ───

const CLINIC_PATIENTS_NAV = [
  { label: 'Create Patient', icon: 'user-plus', path: '/patient/new' },
  { label: 'Register Patient', icon: 'file-plus', path: '/patient/register' },
  { label: "Today's Patients", icon: 'calendar', path: '/patients/today' },
  { label: 'All Patients', icon: 'list', path: '/patients' },
  { label: 'Favorites', icon: 'star', path: '/patients/favorites' },
  // PRD.249 — Outstanding Balances follow-up page, placed below Favorites.
  { label: 'Patient with Outstanding Balances', icon: 'file-text', path: '/clinic/outstanding' },
] as const;

const CLINIC_SETTINGS_NAV = [
  { label: 'Patient Workspace', icon: 'layout-grid', path: '/clinic/patient-workspace' },
  { label: 'Patient Profile', icon: 'clipboard', path: '/clinic/patient-profile' },
  { label: 'Patient Form', icon: 'edit', path: '/clinic/patient-form' },
  { label: 'Vitals', icon: 'activity', path: '/clinic/vitals-definitions' },
  { label: 'Intake Form', icon: 'clipboard', path: '/clinic/intake-form' },
  { label: 'Forms', icon: 'file-text', path: '/clinic/forms' },
  { label: 'Prescription Print', icon: 'file-text', path: '/clinic/prescription-print' },
  { label: 'Invoice Print', icon: 'file-text', path: '/clinic/invoice-print' },
  // PRD.239 — Purchase Order print wizard.
  { label: 'Purchase Order Print', icon: 'file-text', path: '/clinic/purchase-order-print' },
  // PRD.218 — Prescription Template Builder. Lives under Settings alongside
  // other clinical-template management screens (Prescription Print, Charting
  // Templates) rather than under Patients.
  { label: 'Prescription Templates', icon: 'clipboard', path: '/patients/prescription-templates' },
  { label: 'Charting Templates', icon: 'file-text', path: '/clinic/charting-templates' },
] as const;

function PinIcon({ pinned }: { pinned: boolean }) {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill={pinned ? 'currentColor' : 'none'}
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ transform: 'rotate(45deg)' }}
    >
      <line x1="12" y1="17" x2="12" y2="22" />
      <path d="M5 17h14v-1.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V6h1a2 2 0 0 0 0-4H8a2 2 0 0 0 0 4h1v4.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24Z" />
    </svg>
  );
}

function ClinicSidebar({
  isPinned,
  togglePinned,
}: {
  isPinned: (key: string) => boolean;
  togglePinned: (key: string) => void;
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const visiblePatients = useVisibleNav(CLINIC_PATIENTS_NAV);
  const visibleSettings = useVisibleNav(CLINIC_SETTINGS_NAV);

  return (
    <aside
      className="shell-sidebar pc-sidebar"
      style={{
        width: '100%',
        height: '100%',
        overflowX: 'clip',
        overflowY: 'auto',
        borderRadius: 'var(--radius-xl) 0 0 var(--radius-xl)',
        flexShrink: 0,
      }}
    >
      <div style={{ width: '100%', height: '100%' } as React.CSSProperties}>
        {/* Clinic Info Header */}
        <div
          style={{
            padding: 'var(--sp-6)',
            borderBottom: '1px solid var(--border)',
          }}
        >
          {orgLogoHorizontalUrl ? (
            <img
              src={orgLogoHorizontalUrl}
              alt={orgName}
              style={{
                width: '100%',
                maxHeight: 72,
                objectFit: 'contain',
                objectPosition: 'left center',
              }}
            />
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-3)' }}>
              {orgLogoUrl ? (
                <img
                  src={orgLogoUrl}
                  alt={orgName}
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: 'var(--radius-full)',
                    objectFit: 'cover',
                    flexShrink: 0,
                  }}
                />
              ) : (
                <div
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: 'var(--radius-full)',
                    background: 'var(--bg-surface-2)',
                    border: '1px solid var(--border)',
                    flexShrink: 0,
                  }}
                />
              )}
              <span
                style={{
                  fontSize: 'var(--font-size-lg)',
                  fontWeight: 'var(--font-weight-semi)',
                  lineHeight: 'var(--line-height-lg)',
                  letterSpacing: '-0.44px',
                  color: 'var(--text)',
                }}
              >
                {orgName}
              </span>
            </div>
          )}

          {/* Contact rows */}
          <div
            style={{
              marginTop: 'var(--sp-4)',
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--sp-3)',
            }}
          >
            <ContactRow
              icon="map-pin"
              text="123 Medical Plaza, Suite 400&#10;San Francisco, CA 94102"
            />
            {orgPhone && <ContactRow icon="phone" text={orgPhone} />}
            {orgEmail && <ContactRow icon="mail" text={orgEmail} />}
          </div>
        </div>

        <ClinicModePills />

        <div style={{ padding: 'var(--sp-6) var(--sp-6) 0' }}>
          {/* PATIENTS Section */}
          {visiblePatients.length > 0 && (
            <>
              <span
                style={{
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 'var(--font-weight-semi)',
                  lineHeight: 'var(--line-height-sm)',
                  letterSpacing: '0.6px',
                  textTransform: 'uppercase',
                  color: 'var(--text-muted)',
                }}
              >
                Patients
              </span>

              <nav
                style={{
                  marginTop: 'var(--sp-4)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 'var(--sp-1)',
                }}
              >
                {visiblePatients.map((item) => {
                  const active = item.path ? location.pathname === item.path : false;
                  const pinKey = ROUTE_TO_PIN_KEY.get(item.path);
                  const pinned = pinKey ? isPinned(pinKey) : false;
                  return (
                    <div
                      key={item.label}
                      className="nav-pin-row"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        position: 'relative',
                      }}
                    >
                      <a
                        href={item.path || '#'}
                        onClick={(e) => {
                          e.preventDefault();
                          if (item.path) navigate(item.path);
                        }}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 'var(--sp-3)',
                          height: active ? 48 : 44,
                          padding: active ? '0 18px' : '0 16px',
                          borderRadius: 'var(--radius-md)',
                          background: active ? 'var(--gradient-nav-active)' : 'transparent',
                          border: active
                            ? '2px solid var(--color-primary-contrast)'
                            : '2px solid transparent',
                          color: active ? 'var(--text)' : 'var(--text-muted)',
                          textDecoration: 'none',
                          fontSize: 'var(--font-size-base)',
                          fontWeight: 'var(--font-weight-medium)',
                          lineHeight: 'var(--line-height-base)',
                          letterSpacing: '-0.15px',
                          cursor: 'pointer',
                          flex: 1,
                        }}
                      >
                        <NavIcon name={item.icon} />
                        {item.label}
                      </a>
                      {pinKey && (
                        <button
                          onClick={() => togglePinned(pinKey)}
                          className="pin-btn"
                          style={{
                            position: 'absolute',
                            right: 8,
                            width: 28,
                            height: 28,
                            borderRadius: 'var(--radius-sm)',
                            background: 'transparent',
                            border: 'none',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: pinned ? 'var(--color-primary)' : 'var(--text-muted)',
                            cursor: 'pointer',
                            opacity: pinned ? 1 : 0,
                            transition: 'opacity 150ms ease',
                            padding: 0,
                          }}
                        >
                          <PinIcon pinned={pinned} />
                        </button>
                      )}
                    </div>
                  );
                })}
              </nav>
            </>
          )}

          {/* SETTINGS Section */}
          {visibleSettings.length > 0 && (
            <>
              {/* Section divider */}
              <div
                style={{
                  height: 1,
                  background: 'var(--border)',
                  margin: 'var(--sp-3) 0',
                }}
              />

              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <span
                  style={{
                    fontSize: 'var(--font-size-sm)',
                    fontWeight: 'var(--font-weight-semi)',
                    lineHeight: 'var(--line-height-sm)',
                    letterSpacing: '0.6px',
                    textTransform: 'uppercase',
                    color: 'var(--text-muted)',
                  }}
                >
                  Settings
                </span>
                <span
                  style={{
                    height: 19,
                    padding: '0 var(--sp-2)',
                    borderRadius: 'var(--radius-xs)',
                    background: 'var(--color-warning-bg)',
                    color: 'var(--color-warning-text)',
                    fontSize: 'var(--font-size-xs)',
                    fontWeight: 'var(--font-weight-medium)',
                    lineHeight: '19px',
                    letterSpacing: '0.12px',
                  }}
                >
                  Admin only
                </span>
              </div>

              <nav
                style={{
                  marginTop: 'var(--sp-4)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 'var(--sp-1)',
                }}
              >
                {visibleSettings.map((item) => {
                  const active = item.path ? location.pathname === item.path : false;
                  const pinKey = ROUTE_TO_PIN_KEY.get(item.path);
                  const pinned = pinKey ? isPinned(pinKey) : false;
                  return (
                    <div
                      key={item.label}
                      className="nav-pin-row"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        position: 'relative',
                      }}
                    >
                      <a
                        href={item.path || '#'}
                        onClick={(e) => {
                          e.preventDefault();
                          if (item.path) navigate(item.path);
                        }}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 'var(--sp-3)',
                          height: active ? 48 : 44,
                          padding: active ? '0 18px' : '0 16px',
                          borderRadius: 'var(--radius-md)',
                          background: active ? 'var(--gradient-nav-active)' : 'transparent',
                          border: active
                            ? '2px solid var(--color-primary-contrast)'
                            : '2px solid transparent',
                          color: active ? 'var(--text)' : 'var(--text-muted)',
                          textDecoration: 'none',
                          fontSize: 'var(--font-size-base)',
                          fontWeight: 'var(--font-weight-medium)',
                          lineHeight: 'var(--line-height-base)',
                          letterSpacing: '-0.15px',
                          cursor: 'pointer',
                          flex: 1,
                        }}
                      >
                        <NavIcon name={item.icon} />
                        {item.label}
                      </a>
                      {pinKey && (
                        <button
                          onClick={() => togglePinned(pinKey)}
                          className="pin-btn"
                          style={{
                            position: 'absolute',
                            right: 8,
                            width: 28,
                            height: 28,
                            borderRadius: 'var(--radius-sm)',
                            background: 'transparent',
                            border: 'none',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: pinned ? 'var(--color-primary)' : 'var(--text-muted)',
                            cursor: 'pointer',
                            opacity: pinned ? 1 : 0,
                            transition: 'opacity 150ms ease',
                            padding: 0,
                          }}
                        >
                          <PinIcon pinned={pinned} />
                        </button>
                      )}
                    </div>
                  );
                })}
              </nav>
            </>
          )}
        </div>
      </div>
    </aside>
  );
}

// ─── Clinic Sidebar Rail (collapsed) ───

function ClinicSidebarRail({ onExpand }: { onExpand: () => void }) {
  const location = useLocation();
  const navigate = useNavigate();
  const visiblePatients = useVisibleNav(CLINIC_PATIENTS_NAV);
  const visibleSettings = useVisibleNav(CLINIC_SETTINGS_NAV);

  return (
    <aside
      className="shell-sidebar pc-sidebar"
      onClick={onExpand}
      title="Expand sidebar"
      style={{
        width: SIDEBAR_COLLAPSED_WIDTH,
        height: '100%',
        borderRadius: 'var(--radius-xl) 0 0 var(--radius-xl)',
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        paddingTop: 'var(--sp-4)',
        gap: 'var(--sp-1)',
        cursor: 'pointer',
      }}
    >
      {/* Mini logo */}
      {orgLogoUrl ? (
        <img
          src={orgLogoUrl}
          alt={orgName}
          style={{
            width: 32,
            height: 32,
            borderRadius: 'var(--radius-full)',
            objectFit: 'cover',
            flexShrink: 0,
            marginBottom: 'var(--sp-3)',
          }}
        />
      ) : (
        <div
          style={{
            width: 32,
            height: 32,
            borderRadius: 'var(--radius-full)',
            background: 'var(--bg-surface-2)',
            border: '1px solid var(--border)',
            flexShrink: 0,
            marginBottom: 'var(--sp-3)',
          }}
        />
      )}

      {/* Key nav icons */}
      {visiblePatients.map((item) => {
        const active = location.pathname === item.path;
        return (
          <button
            key={item.path}
            title={item.label}
            onClick={(e) => {
              e.stopPropagation();
              navigate(item.path);
            }}
            style={{
              width: 36,
              height: 36,
              borderRadius: 'var(--radius-md)',
              border: 'none',
              background: active ? 'var(--gradient-nav-active)' : 'transparent',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              color: active ? 'var(--text)' : 'var(--text-muted)',
              padding: 0,
            }}
          >
            <NavIcon name={item.icon} />
          </button>
        );
      })}

      {/* Divider */}
      {visiblePatients.length > 0 && visibleSettings.length > 0 && (
        <div
          style={{
            width: 24,
            height: 1,
            background: 'var(--border)',
            margin: 'var(--sp-2) 0',
            flexShrink: 0,
          }}
        />
      )}

      {/* Settings nav icons */}
      {visibleSettings.map((item) => {
        const active = location.pathname === item.path;
        return (
          <button
            key={item.path}
            title={item.label}
            onClick={(e) => {
              e.stopPropagation();
              navigate(item.path);
            }}
            style={{
              width: 36,
              height: 36,
              borderRadius: 'var(--radius-md)',
              border: 'none',
              background: active ? 'var(--gradient-nav-active)' : 'transparent',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              color: active ? 'var(--text)' : 'var(--text-muted)',
              padding: 0,
            }}
          >
            <NavIcon name={item.icon} />
          </button>
        );
      })}
    </aside>
  );
}

// ─── Main Layout ───

export function ClinicShellLayout() {
  const { collapsed, toggle } = useSidebarState('powercareai:clinic-sidebar-collapsed');
  const {
    width: sbWidth,
    dragging,
    startDrag,
    resetWidth,
  } = useResizableSidebar({
    storageKey: 'powercareai:clinic-sidebar-width',
    min: 280,
    max: 520,
    defaultWidth: 320,
    collapsed,
    onExpand: toggle,
  });
  const brandTokens = useBrandTokens();
  const { isPinned, togglePinned } = usePinnedRoutes();

  return (
    <div
      className="pc-desk"
      style={
        {
          ...brandTokens,
          display: 'grid',
          gridTemplateColumns: '1fr',
          gridTemplateRows: 'var(--topbar-height) 1fr',
          height: '100vh',

          fontFamily: 'var(--font-family)',
        } as CSSProperties
      }
    >
      <TopBar sidebarWidth={collapsed ? SIDEBAR_COLLAPSED_WIDTH : sbWidth} />

      {/* Main content area — right of rail, below topbar */}
      <div
        style={{
          padding: 'var(--content-inset)',
          position: 'relative',
          minHeight: 0,
          overflow: 'hidden',
        }}
      >
        {/* Main Card */}
        <div
          style={{
            background: 'transparent',
            borderRadius: 'var(--main-card-radius)',
            boxShadow: 'var(--shadow-paper)',
            height: '100%',
            display: 'flex',
            overflow: 'clip',
          }}
        >
          {/* Sidebar wrapper — width-controlled */}
          <div
            style={{
              width: collapsed ? SIDEBAR_COLLAPSED_WIDTH : sbWidth,
              flexShrink: 0,
              transition: dragging ? 'none' : 'width 200ms ease-out',
              overflow: 'hidden',
            }}
          >
            {collapsed ? (
              <ClinicSidebarRail onExpand={toggle} />
            ) : (
              <ClinicSidebar isPinned={isPinned} togglePinned={togglePinned} />
            )}
          </div>

          <ResizeGutter onDragStart={startDrag} onDoubleClick={resetWidth} />

          {/* Content Area — paper */}
          <div
            style={{
              flex: 1,
              minWidth: 0,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              position: 'relative',
              overflow: 'hidden',
              background: '#ffffff',
              borderRadius: '0 var(--main-card-radius) var(--main-card-radius) 0',
            }}
          >
            {/* Outlet renders page content (header + form) */}
            <Outlet context={{ collapsed, onToggleSidebar: toggle }} />
          </div>
        </div>
      </div>
    </div>
  );
}
